import { InfoIcon } from '@phosphor-icons/react/dist/ssr';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';

type OffPlatformBillingBannerProps = {
  group: GroupSubscriptionData;
  translations?: Record<string, string>;
};

/**
 * Shown on off-platform group cards: a Stripe subscription backs the group but
 * the buyer never pays through Stripe — the publisher bills them externally. We
 * surface who owns billing instead of any Stripe-facing payment control
 * (update card / retry / hosted invoice), which don't apply here.
 */
export function OffPlatformBillingBanner({ group, translations }: OffPlatformBillingBannerProps) {
  if (group.payment_collection_method !== 'off_platform') return null;

  const title = translations?.['group_subscriptions.card.billed_by_publisher_title'] ?? 'Billed by your publisher';
  const bodyTemplate =
    translations?.['group_subscriptions.card.billed_by_publisher_body'] ??
    'Billing is managed separately. Contact {{email}} with any billing-related questions.';

  const bodyNoEmail =
    translations?.['group_subscriptions.card.billed_by_publisher_body_no_email'] ??
    'Billing is managed separately by your publisher.';

  const email = group.publication_contact_email;
  const [before, after] = email ? bodyTemplate.split('{{email}}') : [null, null];

  return (
    <div className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
      <InfoIcon weight="fill" width={20} height={20} className="mt-0.5 shrink-0 text-gray-400" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <p className="font-medium text-gray-900">{title}</p>
        <p>
          {email ? (
            <>
              {before}
              <span className="font-medium text-gray-900">{email}</span>
              {after}
            </>
          ) : (
            bodyNoEmail
          )}
        </p>
      </div>
    </div>
  );
}
